import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlanKey, SchoolDetailDto, SchoolModulesResponse } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { SchoolAdmins } from '../features/SchoolAdmins';
import { ModuleBoard, moduleSummary, type BoardModule } from '../components/ModuleBoard';
import { useToast } from '../components/Toasts';
import { ErrorState, Modal, SchoolStatusChip, SkeletonRows, SkeletonTiles } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { getForFile } from '../lib/download';
import { formatDate, formatINR, formatINRCompact, formatNumber, formatRate } from '../lib/format';
import {
  PricingFields,
  draftToLumpSumPaise,
  draftToPaise,
  paiseToRupeeString,
  type PricingDraft,
} from '../features/PricingFields';

export function SchoolDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [suspending, setSuspending] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [terminating, setTerminating] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [terminateConfirm, setTerminateConfirm] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingDraft, setPricingDraft] = useState<PricingDraft | null>(null);
  const [studentsDraft, setStudentsDraft] = useState('');
  const [draft, setDraft] = useState<Set<string> | null>(null);

  const school = useQuery({
    queryKey: ['school', id],
    queryFn: () => api.get<SchoolDetailDto>(`/api/schools/${id}`),
  });

  const modulesQuery = useQuery({
    queryKey: ['school-modules', id],
    queryFn: () => api.get<SchoolModulesResponse>(`/api/schools/${id}/modules`),
  });

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () =>
      api.get<{
        plans: {
          key: PlanKey;
          name: string;
          tagline: string;
          pricePerStudentPerYearPaise: number | null;
          pricePerStudentPerMonthPaise: number | null;
        }[];
      }>('/api/billing/plans'),
  });

  const [brandOpen, setBrandOpen] = useState(false);
  const [brand, setBrand] = useState({ primaryColor: '#1FA97A', logoUrl: '' });

  const saveBranding = useMutation({
    mutationFn: () =>
      api.patch<SchoolDetailDto>(`/api/schools/${id}/branding`, {
        primaryColor: brand.primaryColor,
        // Empty means "take it down", which is a different instruction from
        // "leave it alone" — so it is sent as null rather than omitted.
        logoUrl: brand.logoUrl.trim() || null,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setBrandOpen(false);
      toast.push('Branding saved', 'success');
    },
    onError: (error) => toast.push(error instanceof Error ? error.message : 'Could not save', 'error'),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const presign = await api.post<{
        fileId: string;
        uploadUrl: string;
        method: string;
        headers: Record<string, string>;
      }>(`/api/schools/${id}/logo`, {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      // Straight to storage, never through this API — the same reason every
      // other upload in this codebase works this way.
      const putResponse = await fetch(presign.uploadUrl, {
        method: presign.method,
        headers: presign.headers,
        body: file,
      });
      if (!putResponse.ok) throw new Error('The upload did not reach storage — try again');

      return api.post<SchoolDetailDto>(`/api/schools/${id}/logo/${presign.fileId}/done`);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setBrand((b) => ({ ...b, logoUrl: updated.logoUrl ?? '' }));
      toast.push('Logo uploaded', 'success');
    },
    onError: (error) =>
      toast.push(
        error instanceof ApiRequestError ? error.message : error instanceof Error ? error.message : 'Could not upload the logo',
        'error',
      ),
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState({ gstNumber: '', latitude: '', longitude: '' });

  const saveDetails = useMutation({
    mutationFn: () =>
      api.patch<SchoolDetailDto>(`/api/schools/${id}/details`, {
        gstNumber: details.gstNumber.trim() || null,
        latitude: details.latitude.trim() === '' ? null : Number(details.latitude),
        longitude: details.longitude.trim() === '' ? null : Number(details.longitude),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setDetailsOpen(false);
      toast.push('School details saved', 'success');
    },
    onError: (error) =>
      toast.push(error instanceof ApiRequestError ? error.message : 'Could not save', 'error'),
  });

  const savePricing = useMutation({
    mutationFn: (input: { paise: number | null; lumpSumPaise: number | null; students: number; note: string }) =>
      api.patch<SchoolDetailDto>(`/api/schools/${id}/pricing`, {
        pricePerStudentPerMonthPaise: input.paise,
        lumpSumPerYearPaise: input.lumpSumPaise,
        billableStudents: input.students,
        note: input.note || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditingPricing(false);
      setPricingDraft(null);
      toast.push('Pricing updated', 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not save pricing', 'error'),
  });

  const modules: BoardModule[] = useMemo(
    () =>
      (modulesQuery.data?.modules ?? []).map((m) => ({
        key: m.key,
        name: m.name,
        description: m.description,
        category: m.category,
        isCore: m.isCore,
        requires: m.requires,
        planDefault: m.planDefault,
      })),
    [modulesQuery.data],
  );

  const saved = useMemo(
    () => new Set((modulesQuery.data?.modules ?? []).filter((m) => m.enabled).map((m) => m.key)),
    [modulesQuery.data],
  );

  const selected = draft ?? saved;
  const dirty = draft !== null && (draft.size !== saved.size || [...draft].some((key) => !saved.has(key)));

  const saveModules = useMutation({
    mutationFn: async (next: Set<string>) => {
      const changes = modules
        .filter((m) => !m.isCore && next.has(m.key) !== saved.has(m.key))
        .map((m) => ({ key: m.key, enabled: next.has(m.key) }));
      return api.patch<SchoolModulesResponse>(`/api/schools/${id}/modules`, { modules: changes });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['school-modules', id], result);
      void queryClient.invalidateQueries({ queryKey: ['school', id] });
      setDraft(null);
      toast.push('Features updated for this school', 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not save features', 'error'),
  });

  const suspend = useMutation({
    mutationFn: () => api.post<SchoolDetailDto>(`/api/schools/${id}/suspend`, { reason: suspendReason }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setSuspending(false);
      setSuspendReason('');
      toast.push(`${updated.name} suspended`, 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not suspend', 'error'),
  });

  const reactivate = useMutation({
    mutationFn: () => api.post<SchoolDetailDto>(`/api/schools/${id}/reactivate`),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast.push(`${updated.name} reactivated`, 'success');
    },
  });

  const securityReset = useMutation({
    mutationFn: () =>
      api.post<{ sessionsEnded: number; accountsFlagged: number }>(`/api/schools/${id}/security-reset`, {
        reason: resetReason,
      }),
    onSuccess: (result) => {
      setResetting(false);
      setResetReason('');
      toast.push(
        `${result.sessionsEnded} signed out · ${result.accountsFlagged} must set a new password`,
        'success',
      );
    },
    onError: (error) =>
      toast.push(error instanceof ApiRequestError ? error.message : 'Could not reset access', 'error'),
  });

  const terminate = useMutation({
    mutationFn: () =>
      api.post<SchoolDetailDto>(`/api/schools/${id}/terminate`, {
        reason: terminateReason,
        confirmName: terminateConfirm,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setTerminating(false);
      setTerminateReason('');
      setTerminateConfirm('');
      toast.push(`${updated.name} closed — records kept`, 'success');
    },
    onError: (error) =>
      toast.push(error instanceof ApiRequestError ? error.message : 'Could not close the account', 'error'),
  });

  const changePlan = useMutation({
    mutationFn: (planKey: PlanKey) => api.post<SchoolDetailDto>(`/api/schools/${id}/plan`, { planKey }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['school-modules', id] });
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setChangingPlan(false);
      setDraft(null);
      toast.push(`Plan changed to ${updated.planName}`, 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not change plan', 'error'),
  });

  if (school.error) {
    return (
      <>
        <PageHeader title="School" />
        <div className="page">
          <ErrorState
            message={school.error instanceof ApiRequestError ? school.error.message : 'Could not load this school'}
            onRetry={() => void school.refetch()}
          />
        </div>
      </>
    );
  }

  if (school.isPending) {
    return (
      <>
        <PageHeader title="Loading…" />
        <div className="page">
          <SkeletonTiles count={5} />
          <SkeletonRows />
        </div>
      </>
    );
  }

  const data = school.data;

  function openPricingEditor() {
    const pricing = school.data?.pricing;
    setPricingDraft({
      custom: pricing?.isCustom ?? false,
      mode: pricing?.isLumpSum ? 'LUMP_SUM' : 'PER_STUDENT',
      rupees: paiseToRupeeString(pricing?.isCustom && !pricing.isLumpSum ? pricing.effectiveMonthlyPaise : null),
      lumpSumRupees: paiseToRupeeString(pricing?.isLumpSum ? pricing.lumpSumPerYearPaise : null),
      note: pricing?.note ?? '',
    });
    setStudentsDraft(String(pricing?.billableStudents ?? 0));
    setEditingPricing(true);
  }

  return (
    <>
      <PageHeader
        title={data.name}
        meta={`School ID · ${data.reference} · ${data.city}, ${data.state} · join code ${data.code}`}
        actions={
          <>
            <button className="btn btn--ghost" onClick={() => navigate('/schools')}>
              All schools
            </button>
            <SchoolStatusChip status={data.status} trialDaysLeft={data.trialDaysLeft} />
          </>
        }
      />

      <div className="page">
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            <Fact label="Plan" value={`${data.planName ?? '—'}${data.billingCycle ? ` · ${data.billingCycle.toLowerCase()}` : ''}`} />
            <Fact label="Students" value={formatNumber(data.studentCount)} mono />
            <Fact label="Admins" value={formatNumber(data.adminCount)} mono />
            <Fact label="Renewal" value={formatDate(data.renewsAt)} mono />
            <Fact label="Support tier" value={data.supportTier.charAt(0) + data.supportTier.slice(1).toLowerCase()} mono />
          </div>

          <div style={{ display: 'flex', gap: 10, borderTop: '1px solid var(--rule)', paddingTop: 16, flexWrap: 'wrap' }}>
            <button className="btn btn--soft" onClick={() => toast.push('Impersonation lands with the School Admin surface', 'info')}>
              Log in as admin
            </button>
            <button className="btn btn--soft" onClick={() => setChangingPlan(true)}>
              Change plan
            </button>
            <button className="btn btn--soft" onClick={() => openPricingEditor()}>
              Edit pricing
            </button>
            <button className="btn btn--soft" onClick={() => navigate(`/billing?school=${data.id}`)}>
              View invoices
            </button>
            {data.status === 'SUSPENDED' ? (
              <button
                className="btn btn--soft"
                style={{ marginLeft: 'auto', color: 'var(--leaf)' }}
                onClick={() => reactivate.mutate()}
                disabled={reactivate.isPending}
              >
                Reactivate
              </button>
            ) : (
              <button className="btn btn--danger" style={{ marginLeft: 'auto' }} onClick={() => setSuspending(true)}>
                Suspend
              </button>
            )}
          </div>
        </div>

        <SchoolAdmins schoolId={id!} />

        <div className="card">
          <div className="card__title">Account &amp; data</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              marginTop: 12,
            }}
          >
            <Row
              title="Download this school's data"
              detail="A zip of spreadsheets — students, staff, attendance, fees, marks and notices — that the school's office can open in Excel. Passwords are stored as one-way hashes and are not in it; accounts.csv lists who has access instead."
              action={
                <button
                  className="btn btn--soft"
                  disabled={exporting}
                  onClick={() => {
                    setExporting(true);
                    getForFile(`/api/schools/${id}/export`, `${data.name}.zip`)
                      .then(() => toast.push('Export downloaded', 'success'))
                      .catch((err) =>
                        toast.push(
                          err instanceof ApiRequestError ? err.message : 'Could not build the export',
                          'error',
                        ),
                      )
                      .finally(() => setExporting(false));
                  }}
                >
                  {exporting ? 'Building…' : 'Download zip'}
                </button>
              }
            />

            <Row
              title="Security reset"
              detail="Signs every member of staff out and makes them set a new password next time they sign in. Parents are untouched — they sign in with a code, not a password."
              action={
                <button className="btn btn--soft" onClick={() => setResetting(true)}>
                  Reset access
                </button>
              }
            />

            <Row
              title="Close this account"
              detail={
                data.status === 'CHURNED'
                  ? 'This school is closed. Its records are all still here — use Reactivate above to reopen it.'
                  : 'Marks the school closed, cancels the subscription and signs everyone out. Nothing is deleted: a school that leaves in March may still need to issue a transfer certificate in June. Reversible.'
              }
              action={
                <button
                  className="btn btn--danger"
                  disabled={data.status === 'CHURNED'}
                  onClick={() => setTerminating(true)}
                >
                  Close account
                </button>
              }
            />
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="card__title">Branding</div>
            {data.primaryColor || data.logoUrl ? null : (
              <span className="tag">Using platform defaults</span>
            )}
            <button
              className="btn btn--soft btn--sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => {
                setBrand({
                  primaryColor: data.primaryColor ?? '#1FA97A',
                  logoUrl: data.logoUrl ?? '',
                });
                setBrandOpen(true);
              }}
            >
              Edit branding
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                aria-hidden="true"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: data.primaryColor ?? 'var(--ink-tint)',
                  border: '1px solid var(--rule)',
                  flex: 'none',
                }}
              />
              <Fact label="Brand colour" value={data.primaryColor ?? 'Not set'} mono />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {data.logoUrl ? (
                <img
                  src={data.logoUrl}
                  alt=""
                  style={{
                    height: 42,
                    width: 42,
                    objectFit: 'contain',
                    borderRadius: 12,
                    border: '1px solid var(--rule)',
                    background: 'var(--white)',
                    flex: 'none',
                  }}
                />
              ) : (
                <div className="school-tile" style={{ height: 42, width: 42 }}>
                  {data.initials}
                </div>
              )}
              <Fact label="Logo" value={data.logoUrl ? 'Uploaded' : 'Initials'} />
            </div>
          </div>

          <div className="caption">
            Used on this school's login screen and through the parent app. Changing
            them here takes effect the next time an app loads — no rebuild needed.
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="card__title">Registration details</div>
            <button
              className="btn btn--soft btn--sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => {
                setDetails({
                  gstNumber: data.gstNumber ?? '',
                  latitude: data.latitude !== null ? String(data.latitude) : '',
                  longitude: data.longitude !== null ? String(data.longitude) : '',
                });
                setDetailsOpen(true);
              }}
            >
              Edit details
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <Fact label="GST number" value={data.gstNumber ?? 'Not set'} mono />
            <Fact
              label="Campus coordinates"
              value={data.latitude !== null && data.longitude !== null ? `${data.latitude}, ${data.longitude}` : 'Not set'}
              mono
            />
            <Fact label="Bus geofence" value={data.latitude !== null ? 'Ready' : 'Needs coordinates'} />
          </div>

          <div className="caption">
            Coordinates are also what Transport uses to end a bus run automatically
            when it gets back to school.
          </div>
        </div>

        {data.pricing ? (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div className="card__title">Pricing</div>
              {data.pricing.isLumpSum ? (
                <span className="tag tag--manual">Lump sum</span>
              ) : data.pricing.isCustom ? (
                <span className="tag tag--manual">Negotiated rate</span>
              ) : (
                <span className="tag">{data.planName} list price</span>
              )}
              <button className="btn btn--soft btn--sm" style={{ marginLeft: 'auto' }} onClick={() => openPricingEditor()}>
                Edit pricing
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              {data.pricing.isLumpSum ? (
                <Fact
                  label="Flat fee / year"
                  value={data.pricing.lumpSumPerYearPaise === null ? 'Not set' : formatINR(data.pricing.lumpSumPerYearPaise)}
                  mono
                />
              ) : (
                <Fact
                  label="Per student / month"
                  value={data.pricing.effectiveMonthlyPaise === null ? 'Not set' : formatRate(data.pricing.effectiveMonthlyPaise)}
                  mono
                />
              )}
              <Fact label="Billable students" value={formatNumber(data.pricing.billableStudents)} mono />
              <Fact label="Per month" value={formatINR(Math.round(data.pricing.annualAmountPaise / 12))} mono />
              <Fact label="Per year" value={formatINR(data.pricing.annualAmountPaise)} mono />
              <Fact
                label={data.billingCycle === 'ANNUAL' ? 'Each invoice · annual' : 'Each invoice · monthly'}
                value={formatINR(data.pricing.cycleAmountPaise)}
                mono
              />
            </div>

            {data.pricing.isCustom ? (
              <div className="caption" style={{ borderTop: '1px solid var(--rule)', paddingTop: 12 }}>
                {data.pricing.planListMonthlyPaise !== null ? (
                  <>
                    List price is {formatRate(data.pricing.planListMonthlyPaise)}/student/month
                    {data.pricing.discountBps !== null ? (
                      <>
                        {' '}
                        —{' '}
                        <strong style={{ color: data.pricing.discountBps < 0 ? 'var(--leaf)' : 'var(--marigold-deep)' }}>
                          {data.pricing.discountBps < 0
                            ? `${Math.abs(data.pricing.discountBps / 100).toFixed(1)}% discount`
                            : `${(data.pricing.discountBps / 100).toFixed(1)}% above list`}
                        </strong>
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  <>{data.planName} has no list price; this school is priced per deal.</>
                )}
                {data.pricing.note ? ` ${data.pricing.note}` : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid-4">
          <StatCard label="MRR" value={formatINRCompact(data.mrrPaise)} caption={data.billingCycle === 'ANNUAL' ? 'annual contract ÷ 12' : 'billed monthly'} />
          <StatCard label="Active parents" value={formatNumber(data.activeParentCount)} caption={`${Math.round((data.activeParentCount / Math.max(data.studentCount, 1)) * 100)}% of students`} />
          <StatCard label="Board" value={data.board} caption={`Onboarded ${formatDate(data.onboardedAt)}`} />
          <StatCard label="Features on" value={`${selected.size + modules.filter((m) => m.isCore && !selected.has(m.key)).length}`} caption={`of ${modules.length} modules`} />
        </div>

        {/* Per-school feature provisioning */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="card__title">Features</div>
            <span className="caption">{modules.length > 0 ? moduleSummary(modules, selected) : ''}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
              {dirty ? (
                <>
                  <button className="btn btn--soft" onClick={() => setDraft(null)} disabled={saveModules.isPending}>
                    Discard
                  </button>
                  <button className="btn btn--sm" onClick={() => saveModules.mutate(selected)} disabled={saveModules.isPending}>
                    {saveModules.isPending ? 'Saving…' : 'Save changes'}
                  </button>
                </>
              ) : (
                <span className="caption">
                  Inherited from the {modulesQuery.data?.planKey ?? '—'} plan; overrides are kept if the plan changes.
                </span>
              )}
            </div>
          </div>

          {modulesQuery.isPending ? (
            <SkeletonRows count={3} />
          ) : (
            <ModuleBoard modules={modules} selected={selected} onChange={setDraft} />
          )}
        </div>
      </div>

      {brandOpen ? (
        <Modal
          title="Branding"
          onClose={() => setBrandOpen(false)}
          footer={
            <>
              <button className="btn" disabled={saveBranding.isPending} onClick={() => saveBranding.mutate()}>
                {saveBranding.isPending ? 'Saving…' : 'Save branding'}
              </button>
              <button className="btn btn--ghost" onClick={() => setBrandOpen(false)}>
                Cancel
              </button>
            </>
          }
        >
          <div className="field">
            <span>Brand colour</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                aria-label="Brand colour"
                value={brand.primaryColor}
                onChange={(e) => setBrand((b) => ({ ...b, primaryColor: e.target.value }))}
                style={{
                  width: 44,
                  height: 36,
                  padding: 2,
                  border: '1px solid var(--rule)',
                  borderRadius: 8,
                  background: 'var(--white)',
                  cursor: 'pointer',
                  flex: 'none',
                }}
              />
              <input
                value={brand.primaryColor}
                onChange={(e) => setBrand((b) => ({ ...b, primaryColor: e.target.value }))}
                placeholder="#1FA97A"
                spellCheck={false}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </div>
          </div>

          <div className="field">
            <span>Logo</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt=""
                  style={{
                    height: 42,
                    width: 42,
                    objectFit: 'contain',
                    borderRadius: 12,
                    border: '1px solid var(--rule)',
                    background: 'var(--white)',
                    flex: 'none',
                  }}
                />
              ) : (
                <div className="school-tile" style={{ height: 42, width: 42 }}>
                  {data.initials}
                </div>
              )}
              <label className="btn btn--soft btn--sm" style={{ cursor: 'pointer' }}>
                {uploadLogo.isPending ? 'Uploading…' : 'Upload logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: 'none' }}
                  disabled={uploadLogo.isPending}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadLogo.mutate(file);
                    e.target.value = '';
                  }}
                />
              </label>
              {brand.logoUrl ? (
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={uploadLogo.isPending}
                  onClick={() => setBrand((b) => ({ ...b, logoUrl: '' }))}
                >
                  Remove
                </button>
              ) : null}
            </div>
            <span className="field__hint">
              PNG, JPEG or WEBP, up to 2MB. Removing goes back to the school's initials once you
              save.
            </span>
          </div>
        </Modal>
      ) : null}

      {detailsOpen ? (
        <Modal
          title="Registration details"
          onClose={() => setDetailsOpen(false)}
          footer={
            <>
              <button
                className="btn"
                disabled={saveDetails.isPending || !detailsValid(details)}
                onClick={() => saveDetails.mutate()}
              >
                {saveDetails.isPending ? 'Saving…' : 'Save details'}
              </button>
              <button className="btn btn--ghost" onClick={() => setDetailsOpen(false)}>
                Cancel
              </button>
            </>
          }
        >
          <label className="field">
            <span>GST number</span>
            <input
              value={details.gstNumber}
              onChange={(e) => setDetails((d) => ({ ...d, gstNumber: e.target.value.toUpperCase() }))}
              placeholder="27AAAAA0000A1Z5"
              spellCheck={false}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <span className="field__hint">
              Clear the box if this school has no GST registration.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 12 }}>
            <label className="field" style={{ flex: 1 }}>
              <span>Latitude</span>
              <input
                value={details.latitude}
                onChange={(e) => setDetails((d) => ({ ...d, latitude: e.target.value }))}
                placeholder="18.5204"
                inputMode="decimal"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span>Longitude</span>
              <input
                value={details.longitude}
                onChange={(e) => setDetails((d) => ({ ...d, longitude: e.target.value }))}
                placeholder="73.8567"
                inputMode="decimal"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </label>
          </div>
          <span className="field__hint">
            Where the gates are — leave both blank to clear. This is also what a bus
            run needs to end itself automatically when it gets back to school.
          </span>
        </Modal>
      ) : null}

      {resetting ? (
        <Modal
          title={`Reset access for ${data.name}?`}
          onClose={() => setResetting(false)}
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setResetting(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto', background: 'var(--coral)', color: '#fff' }}
                onClick={() => securityReset.mutate()}
                disabled={resetReason.trim().length < 3 || securityReset.isPending}
              >
                {securityReset.isPending ? 'Resetting…' : 'Sign everyone out'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Every member of staff is signed out of the dashboard and must set a new password the next
            time they sign in. Parents are not affected — they sign in with a code sent to their phone,
            so there is no password of theirs to reset. Nothing is deleted.
          </p>
          <label className="field">
            <span>Reason</span>
            <textarea
              rows={3}
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="Office laptop lost on 16 September"
            />
          </label>
        </Modal>
      ) : null}

      {terminating ? (
        <Modal
          title={`Close ${data.name}?`}
          onClose={() => setTerminating(false)}
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setTerminating(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto', background: 'var(--coral)', color: '#fff' }}
                onClick={() => terminate.mutate()}
                disabled={
                  terminateReason.trim().length < 3 ||
                  terminateConfirm !== data.name ||
                  terminate.isPending
                }
              >
                {terminate.isPending ? 'Closing…' : 'Close this account'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            The school is marked closed, its subscription is cancelled and everyone is signed out.
            <strong> Nothing is deleted</strong> — every student, fee and attendance record stays exactly
            where it is, so a transfer certificate can still be issued months later. You can reopen the
            account at any time with Reactivate.
          </p>
          <p className="caption" style={{ margin: 0 }}>
            Consider downloading their data first, so the school has its own copy.
          </p>
          <label className="field">
            <span>Reason</span>
            <textarea
              rows={2}
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
              placeholder="Did not renew for 2027-28"
            />
          </label>
          <label className="field">
            <span>Type the school's name to confirm</span>
            <input
              value={terminateConfirm}
              onChange={(e) => setTerminateConfirm(e.target.value)}
              placeholder={data.name}
              autoComplete="off"
            />
            <span className="field__hint">
              Exactly as it appears above. This is the only thing standing between a mis-click and the
              wrong school being closed.
            </span>
          </label>
        </Modal>
      ) : null}

      {suspending ? (
        <Modal
          title={`Suspend ${data.name}?`}
          onClose={() => setSuspending(false)}
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setSuspending(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto', background: 'var(--coral)', color: '#fff' }}
                onClick={() => suspend.mutate()}
                disabled={suspendReason.trim().length < 3 || suspend.isPending}
              >
                {suspend.isPending ? 'Suspending…' : 'Suspend school'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Staff and parents lose access immediately. Data is retained and the school can be reactivated. This is
            recorded in the audit log against your account.
          </p>
          <label className="field">
            <span>Reason</span>
            <textarea
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Non-payment after three reminders"
            />
          </label>
        </Modal>
      ) : null}

      {editingPricing && pricingDraft ? (
        <Modal
          title="Pricing for this school"
          onClose={() => setEditingPricing(false)}
          wide
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setEditingPricing(false)} disabled={savePricing.isPending}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto' }}
                disabled={
                  savePricing.isPending ||
                  (pricingDraft.custom && !hasCustomPricingValue(pricingDraft)) ||
                  (data.pricing?.planListMonthlyPaise === null && !hasCustomPricingValue(pricingDraft))
                }
                onClick={() =>
                  savePricing.mutate({
                    paise: draftToPaise(pricingDraft),
                    lumpSumPaise: draftToLumpSumPaise(pricingDraft),
                    students: Number(studentsDraft) || 0,
                    note: pricingDraft.note.trim(),
                  })
                }
              >
                {savePricing.isPending ? 'Saving…' : 'Save pricing'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Changing this re-prices the next invoice, not ones already issued. The change is recorded in the audit
            log against your account.
          </p>

          <label className="field" style={{ maxWidth: 220 }}>
            <span>Billable students</span>
            <input
              inputMode="numeric"
              value={studentsDraft}
              onChange={(e) => setStudentsDraft(e.target.value.replace(/\D/g, ''))}
            />
          </label>

          <PricingFields
            draft={pricingDraft}
            onChange={setPricingDraft}
            planName={data.planName ?? 'This plan'}
            planListMonthlyPaise={data.pricing?.planListMonthlyPaise ?? null}
            students={Number(studentsDraft) || 0}
            billingCycle={data.billingCycle ?? 'ANNUAL'}
          />
        </Modal>
      ) : null}

      {changingPlan ? (
        <Modal
          title="Change plan"
          onClose={() => setChangingPlan(false)}
          footer={
            <button className="btn btn--ghost" onClick={() => setChangingPlan(false)}>
              Cancel
            </button>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Features you set by hand for this school are kept. Everything else follows the new plan.
            {data.pricing?.isLumpSum ? (
              <>
                {' '}
                This school's lump sum of <strong>{formatINR(data.pricing.lumpSumPerYearPaise ?? 0)}/year</strong> is
                also kept — use <em>Edit pricing</em> afterwards to move it to the new list price.
              </>
            ) : data.pricing?.isCustom ? (
              <>
                {' '}
                This school's negotiated rate of{' '}
                <strong>{formatRate(data.pricing.effectiveMonthlyPaise ?? 0)}/student/month</strong> is also kept —
                use <em>Edit pricing</em> afterwards to move it to the new list price.
              </>
            ) : null}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(plans.data?.plans ?? []).map((plan) => (
              <button
                key={plan.key}
                className="module-row"
                style={{ cursor: 'pointer', textAlign: 'left', borderColor: plan.key === data.planKey ? 'var(--marigold)' : undefined }}
                onClick={() => changePlan.mutate(plan.key)}
                disabled={changePlan.isPending}
              >
                <div style={{ flex: 1 }}>
                  <div className="module-row__name">
                    {plan.name}
                    {plan.key === data.planKey ? <span className="tag">Current</span> : null}
                  </div>
                  <div className="module-row__desc">{plan.tagline}</div>
                </div>
                <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                  {plan.pricePerStudentPerMonthPaise
                    ? `${formatRate(plan.pricePerStudentPerMonthPaise)}/student/mo`
                    : 'Custom'}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/** Whichever mode is picked has a real value typed into it. */
function hasCustomPricingValue(draft: PricingDraft): boolean {
  return draft.mode === 'LUMP_SUM' ? draftToLumpSumPaise(draft) !== null : draftToPaise(draft) !== null;
}

/** Both coordinates blank (clearing), or both present and in range — never one alone. */
function detailsValid(details: { latitude: string; longitude: string }): boolean {
  const lat = details.latitude.trim();
  const lng = details.longitude.trim();
  if (lat === '' && lng === '') return true;
  if (lat === '' || lng === '') return false;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  return Number.isFinite(latNum) && Number.isFinite(lngNum) && Math.abs(latNum) <= 90 && Math.abs(lngNum) <= 180;
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span className="label" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
        {label}
      </span>
      <span className={mono ? 'mono' : ''} style={{ fontSize: 14.5, fontWeight: mono ? 600 : 700 }}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="tile__value" style={{ fontSize: 26 }}>
        {value}
      </div>
      <div className="caption">{caption}</div>
    </div>
  );
}

/**
 * One irreversible-ish action, spelled out.
 *
 * The explanation sits next to the button rather than only inside the confirm
 * dialog: someone deciding whether to press "Close account" needs to know it
 * keeps the data *before* they click, not after.
 */
function Row({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        paddingTop: 14,
        borderTop: '1px solid var(--rule)',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
        <div className="caption" style={{ marginTop: 3, lineHeight: 1.5 }}>
          {detail}
        </div>
      </div>
      <div style={{ flex: 'none' }}>{action}</div>
    </div>
  );
}
